import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { resetConnection, setReconnecting } from '../store/connectionSlice';
import { clearConversation, updateSpeechRateAnalysis } from '../store/conversationSlice';
import { logoutUser } from '../store/authSlice';
import { websocketService } from '../services/websocketService';
import { cleanupService } from '../services/cleanupService';
import { getAuth, signOut } from 'firebase/auth';
import TopBar from '../components/TopBar';
import ConversationFeed from '../components/ConversationFeed';
import { AlertModal } from '../components/ui/AlertModal';
import HotButtonFocus from '../components/HotButtonFocus';
import PaneResizeHandle from '../components/PaneResizeHandle';
import { CombinedInfoBar } from '../components/CombinedInfoBar';
import { MeetBotModal } from '../components/MeetBotModal';
import { ResumeSessionModal } from '../components/ResumeSessionModal';
import { ManualCaptureModal } from '../components/ManualCaptureModal';
import { speechRateService } from '../services/speechRateService';
import { conversationService } from '../services/conversationService';
import CompetitorAlert from '../components/CompetitorAlert';
import { landmineWebSocketService } from '../services/landmineWebSocketService';
import { competitorWebSocketService } from '../services/competitorWebSocketService';
import { playbookChecklistWebSocketService } from '../services/playbookChecklistWebSocketService';
import { removeCompetitorAlert } from '../store/slices/competitorSlice';
import { calendarWebSocketService, CalendarBot } from '../services/calendarWebSocketService';
import LiveReconnectOverlay from '../components/LiveReconnectOverlay';
import RosterEditNotice from '../components/RosterEditNotice';
import UserRosterButton from '../components/UserRosterButton';
import WatchSessionModal from '../components/WatchSessionModal';
import WatcherStatus from '../components/WatcherStatus';
import LiveSessionHeader from '../components/LiveSessionHeader';
import CoSellerStatus from '../components/CoSellerStatus';
import BotNotAdmittedNotice from '../components/BotNotAdmittedNotice';
import type { BotNotAdmittedReason } from '../constants/botAdmission';
import SpeechRateBanner from '../components/SpeechRateBanner';
import { sessionManager } from '../services/sessionManager';
import { config } from '../config';
import { EndOfCallRequest, useEndOfCallFlow } from '../hooks/useEndOfCallFlow';
import { useBotEndedOutside } from '../hooks/useBotEndedOutside';
import { endedSelectedBot } from '../utils/endedSelectedBot';
import { useStandaloneWatcher } from '../hooks/useStandaloneWatcher';
import { useRestoreHistoryOnReconnect } from '../hooks/useRestoreHistoryOnReconnect';
import { joinBotSession } from '../services/botSessionJoin';
import { restoreOwnPlaybookUsage } from '../services/ownSessionRecord';
import { clearLiveSessionData } from '../services/liveSessionData';
import { disconnectLiveCallSockets } from '../services/liveCallSockets';
import EndOfCallModals from '../components/EndOfCallModals';
import ParticipantRolesModal from '../components/ParticipantRolesModal';
import SalesPlaybookPanel from '../components/SalesPlaybookPanel';
import { SessionViewerProvider } from '../contexts/SessionViewerContext';
import { resolveOwnBotParticipantId } from '../utils/ownParticipant';
import { resolveLiveViewerRole } from '../utils/liveViewerRole';
import { COSELLER_MIC_COPY, STANDALONE_JOIN_MODE, WATCHER_COPY } from '../constants/watchSession';
import type { WatchableSession } from '../types/liveSession';
import { Button } from '../components/ui/button';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// The live call's pane widths a drag must leave (the macOS minimums), and each handle's width.
const PANE_MIN = { left: 350, centre: 380, hot: 210, handle: 8 };

/** The pane row's content box (inside its padding), in viewport pixels. */
const paneRowBox = (row: HTMLDivElement | null) => {
  if (!row) return null;
  const rect = row.getBoundingClientRect();
  const style = window.getComputedStyle(row);
  const paddingLeft = parseFloat(style.paddingLeft || '0');
  const width = rect.width - paddingLeft - parseFloat(style.paddingRight || '0');
  return width > 0 ? { left: rect.left + paddingLeft, width } : null;
};

const ConversationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isConnected } = useSelector((state: RootState) => state.connection);
  const { transcripts, speechRateSettings, participants } = useSelector((state: RootState) => state.conversation);
  const { isAuthenticated, user, tokenInfo, isMongoVerified } = useSelector((state: RootState) => state.auth);
  // Hot buttons are gated by the checklist service (HOT_BUTTONS_ENABLED): off means two panes.
  const hotButtonsEnabled = useSelector((state: RootState) => state.hotButton.enabled);
  // Draggable pane widths, as on macOS and WPF (owner, 2026-10-26): null keeps the
  // default split (50/50, or 40/40/20 with hot buttons); a drag sets a percentage
  // of the row. Minimums are the macOS ones.
  const paneRowRef = useRef<HTMLDivElement>(null);
  const [leftPanePct, setLeftPanePct] = useState<number | null>(null);
  const [hotPanePct, setHotPanePct] = useState<number | null>(null);
  const [isAudioCaptureActive, setIsAudioCaptureActive] = useState(false);
  // Ref + state for the fixed header (navbar + speech alert + info bar)
  // Used to set the main content marginTop dynamically so nothing is hidden behind the header
  const fixedHeaderRef = useRef<HTMLDivElement>(null);
  const [fixedHeaderHeight, setFixedHeaderHeight] = useState(92);
  // isSavingSession state removed - session saving now handled by saver_service
  const [showMeetBotModal, setShowMeetBotModal] = useState(false);
  const [showWatchModal, setShowWatchModal] = useState(false);
  // Incremented by the "User Roster" button to reopen the roles modal on demand.
  const [rosterOpenRequest, setRosterOpenRequest] = useState(0);
  const [isBotConnected, setIsBotConnected] = useState(false);

  // The watch modal shows a connecting state while a join is in flight and
  // closes itself once the bot connection is live.
  useEffect(() => {
    if (isBotConnected) setShowWatchModal(false);
  }, [isBotConnected]);

  // Calendar integration state
  const [calendarBots, setCalendarBots] = useState<CalendarBot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [connectingBotId, setConnectingBotId] = useState<string | null>(null); // Loading state for bot connection
  // In-app notice for a failed bot action (replaces window.alert); the flow goes on without waiting for OK
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  // Only the setter is needed since the inline BotSelector was replaced by the watch modal.
  const [, setIsCalendarConnected] = useState(false);

  // Track bot type and session info for removal
  const [currentBotType, setCurrentBotType] = useState<'manual' | 'calendar' | null>(null);
  const [currentBotSessionId, setCurrentBotSessionId] = useState<string | null>(null);
  const [currentBotDisplayId, setCurrentBotDisplayId] = useState<string | null>(null); // For displaying in modal
  // Whether this user owns (added) the connected bot — gates the roster (the
  // "User Roster" button and its auto-opening modal; the services refuse a
  // non-owner's roster edit) and the end-of-call participant review. The web
  // gets no owner uid; ownership comes from the source of each join: a bot this
  // user just created, the calendar socket (it lists only the signed-in user's
  // bots) and the server's `isOwn` on /api/bots/active. handleBotSelection
  // defaults it to false, so a path that does not say is treated as a watcher.
  const [isSessionCreator, setIsSessionCreator] = useState(false);
  // The name of someone else's bot call this user watches (from its saved session).
  const [watchedBotSessionName, setWatchedBotSessionName] = useState<string | null>(null);
  // The roster row the bot's owner marked "Me" (local only): their alerts say "You said".
  const [markedOwnParticipantId, setMarkedOwnParticipantId] = useState<string | null>(null);
  // Why the page's bot was never let into its meeting ("Bot not let in"), shown
  // until the rep dismisses it or starts another session (owner, 2026-10-03).
  const [botNotAdmittedReason, setBotNotAdmittedReason] = useState<BotNotAdmittedReason | null>(null);


  // Session resume state
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumableBots, setResumableBots] = useState<CalendarBot[]>([]);
  /** The bot being resumed from the Resume modal, while its join runs. */
  const [resumingBotId, setResumingBotId] = useState<string | null>(null);

  // Manual audio capture state
  const [showManualCaptureModal, setShowManualCaptureModal] = useState(false);

  // Calendar settings (fetched on mount for bot name)
  const [calendarSettings, setCalendarSettings] = useState<{ botName?: string } | null>(null);

  // End-of-call flow (participant review, then summary) — one flow whether the
  // call ended via Remove Bot or outside the app.
  const endOfCallFlow = useEndOfCallFlow();
  const { startEndOfCall, endFromOutside, skipEndOfCall } = endOfCallFlow;

  // The calendar callbacks are registered once, so they read the connected
  // bot through this ref instead of a stale closure.
  const connectedBotRef = useRef({
    isBotConnected: false,
    connectedBotIds: [] as Array<string | null>,
    isSessionCreator: false,
  });
  connectedBotRef.current = {
    isBotConnected,
    connectedBotIds: [currentBotDisplayId, selectedBotId],
    isSessionCreator,
  };

  // session_ended carries only the room id; the calendar list has its title.
  const calendarBotsRef = useRef<CalendarBot[]>([]);
  calendarBotsRef.current = calendarBots;

  const handleSessionEnded = React.useCallback(async (request: EndOfCallRequest) => {
    setShowMeetBotModal(false);
    await startEndOfCall(request);
  }, [startEndOfCall]);

  const clearBotState = React.useCallback(() => {
    setSelectedBotId(null);
    setIsBotConnected(false);
    setCurrentBotType(null);
    setCurrentBotSessionId(null);
    setCurrentBotDisplayId(null);
    setIsSessionCreator(false);
    setWatchedBotSessionName(null);
    setMarkedOwnParticipantId(null);
  }, []);

  const closeMeetBotModal = React.useCallback(() => setShowMeetBotModal(false), []);

  /**
   * The page's bot ended without ever being let in: no review or summary (there
   * was no call). The lobby closes, the page returns to the start screen and
   * shows why; a new session can start at once.
   */
  const handleBotNotAdmitted = React.useCallback((reason: BotNotAdmittedReason) => {
    setBotNotAdmittedReason(reason);
    setShowMeetBotModal(false);
    clearLiveSessionData();
    clearBotState();
  }, [clearBotState]);

  /** Starting another session (or Dismiss) replaces "Bot not let in". */
  const clearBotNotAdmitted = React.useCallback(() => setBotNotAdmittedReason(null), []);
  // The page's End flow for a bot call: the Remove Bot modal, which confirms first.
  const openMeetBotModal = React.useCallback(() => setShowMeetBotModal(true), []);

  /**
   * Bot ended outside the app (left/removed from the meeting, terminated,
   * failed, or the recall socket's session_ended): the end-of-call flow once,
   * and every live socket closed.
   */
  const handleBotEndedOutside = useBotEndedOutside({
    endFromOutside,
    skipEndOfCall,
    connectedBotRef,
    calendarBotsRef,
    onEndOfCallStarted: closeMeetBotModal,
    onConnectedBotGone: clearBotState,
    onBotNotAdmitted: handleBotNotAdmitted,
  });

  // Watching someone's desktop call (read-only observer) or co-selling it with
  // the user's own mic (D6, 2026-10-05); its end shows the summary.
  const {
    watchedSession: watchedStandalone,
    joiningSessionId: joiningStandaloneId,
    isJoiningWithMic,
    joinError: standaloneJoinError,
    watch: watchStandalone,
    joinWithMic,
    leave: leaveStandalone,
  } = useStandaloneWatcher({ onSessionEnded: handleSessionEnded });

  // Whatever the call's own socket missed while it was down comes back as silent history.
  useRestoreHistoryOnReconnect();

  const isWatchingStandalone = !!watchedStandalone;
  // On someone's desktop call with the user's own mic: their lines, their speech rate.
  const isCoSeller = watchedStandalone?.role === STANDALONE_JOIN_MODE.salesperson;
  // Someone else's call, watched or co-sold: read-only, Leave instead of the owner's End.
  const isWatcher = (isBotConnected && !isSessionCreator) || isWatchingStandalone;
  const isSessionLive = isBotConnected || isWatchingStandalone;
  // Both bot calls and desktop-hosted standalone calls consume the persisted
  // checklist stream. Transcript producers feed the detector directly.
  const checklistRoomId = watchedStandalone?.roomId || (isBotConnected ? currentBotDisplayId : null);
  useEffect(() => {
    if (checklistRoomId) {
      void playbookChecklistWebSocketService.connect(checklistRoomId).catch(error => {
        console.warn('[PlaybookChecklist] Connection failed', error);
      });
    }
    return () => playbookChecklistWebSocketService.disconnect();
  }, [checklistRoomId]);
  const watchedSessionName = watchedStandalone?.sessionName ?? watchedBotSessionName;
  // The live session header's name: the watched call's, else the owner's bot's meeting.
  const liveSessionName =
    watchedSessionName ?? calendarBots?.find((bot) => bot?.botId === currentBotDisplayId)?.eventTitle ?? null;
  // The header's row 1: "Live" (owner), "Live · Co-Seller" or "Live · Observer".
  const liveViewerRole = resolveLiveViewerRole({ isCoSeller, isWatcher });
  const getCallStartTime = useCallback(() => sessionManager.getSessionInfo()?.callStartTime ?? null, []);
  // A mark belongs to one bot call: recall ids repeat across calls.
  useEffect(() => setMarkedOwnParticipantId(null), [currentBotDisplayId]);

  // The viewer's own participant on a bot call: alert labels ("You said") and
  // the speech-rate banner match on it. The owner's "Me" row, or the
  // salesperson row carrying this user's email (a second salesperson watching
  // the bot included); a watcher matching no salesperson has none.
  const ownBotParticipantId = isBotConnected
    ? resolveOwnBotParticipantId({
        participants,
        markedParticipantId: isSessionCreator ? markedOwnParticipantId : null,
        email: user?.email,
        displayName: user?.displayName,
        isOwner: isSessionCreator,
      })
    : null;
  const ownParticipantId = isCoSeller ? watchedStandalone?.participantId ?? null : ownBotParticipantId;

  const handleWatchStandalone = React.useCallback(async (session: WatchableSession) => {
    clearBotNotAdmitted();
    if (await watchStandalone(session)) setShowWatchModal(false);
  }, [watchStandalone, clearBotNotAdmitted]);

  // From the choice step's Join as Co-Seller click: joinWithMic opens the mic before it awaits anything.
  const handleJoinWithMic = React.useCallback(async (session: WatchableSession, deviceId: string | null) => {
    clearBotNotAdmitted();
    if (await joinWithMic(session, deviceId, user?.displayName)) setShowWatchModal(false);
  }, [joinWithMic, clearBotNotAdmitted, user?.displayName]);

  /**
   * A watcher or co-seller leaves (header or reconnect overlay): close their
   * own sockets (a co-seller says `leave_session` first and releases the mic)
   * and return to the start screen. Nothing ends for anyone else.
   */
  const handleLeaveWatching = React.useCallback(() => {
    if (isWatchingStandalone) {
      leaveStandalone();
      return;
    }
    disconnectLiveCallSockets();
    clearLiveSessionData();
    clearBotState();
  }, [isWatchingStandalone, leaveStandalone, clearBotState]);

  // Get alerts from Redux store. Landmines render in the CombinedInfoBar's
  // Landmines column (LandmineBar), not as corner cards.
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.alerts || []);

  // Initialize bot connected state - clear localStorage on mount since bot connection
  // should only be set when actively connecting via MeetBotModal
  // Also generate unique clientId for this session
  useEffect(() => {

    // Clear any stale bot info from localStorage on component mount
    // This ensures a fresh start and prevents showing "Remove Bot" when no bot is connected
    localStorage.removeItem('salesguru_bot_id');
    localStorage.removeItem('salesguru_session_id');
    localStorage.removeItem('salesguru_is_salesperson_host');
    setIsBotConnected(false);
  }, []);

  // Fetch calendar settings on mount (non-blocking) for bot name default
  useEffect(() => {
    const fetchCalendarSettings = async () => {
      try {
        const token = tokenInfo?.token;
        if (!token) return;

        const backendUrl = config.server.backendUrl || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/api/calendar/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setCalendarSettings(data.settings);
          }
        }
      } catch (error) {
        // Non-blocking - silently fail, will use default bot name
        console.debug('[ConversationPage] Could not fetch calendar settings:', error);
      }
    };

    fetchCalendarSettings();
  }, [tokenInfo?.token]);

  // Clear bot state once calendar_sync says the selected bot ended (terminated or
  // failed), so "Remove Bot" updates when a bot ends outside the app (e.g. removed
  // from Google Meet). A bot missing from the list ends nothing: a manual bot only
  // reaches the list with calendar_sync's next status poll, and clearing on its
  // absence dropped a live call whose bot was let in sooner (local, 2026-10-03).
  useEffect(() => {
    // A watcher's bot is someone else's: the calendar list (this user's bots
    // only) never holds it, so its absence means nothing. A watched call's end
    // arrives on the recall socket (session_ended, useBotEndedOutside).
    if (!isBotConnected || !currentBotDisplayId || !isSessionCreator) {
      return; // Nothing to check
    }

    const endedBot = endedSelectedBot(calendarBots, currentBotDisplayId);
    if (endedBot) {
      // Run the end-of-call flow before the state it reads is cleared (a no-op
      // for an already-ended call).
      handleBotEndedOutside(endedBot);
      console.log('📅 Selected bot ended, clearing bot connected state');
      setIsBotConnected(false);
      setCurrentBotType(null);
      setCurrentBotSessionId(null);
      setCurrentBotDisplayId(null);
      setIsSessionCreator(false);
      setSelectedBotId(null);
    }
  }, [calendarBots, isBotConnected, currentBotDisplayId, currentBotSessionId, isSessionCreator, handleBotEndedOutside]);

  // OLD disabled useEffect - kept for reference
  // useEffect(() => {
  //   // Check if there are any active/joining/creating bots
  //   const hasActiveBots = calendarBots.some(bot =>
  //     bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
  //   );

  //   // If we have active bots but isBotConnected is false, update it
  //   if (hasActiveBots && !isBotConnected) {
  //     console.log('📅 Found active calendar bots, setting isBotConnected = true');
  //     setIsBotConnected(true);

  //     // Also set the current bot info if we have a selected bot
  //     const activeBot = calendarBots.find(b => b.botId === selectedBotId);
  //     if (activeBot) {
  //       setCurrentBotType('calendar');
  //       setCurrentBotSessionId(activeBot.botSessionId);
  //       setCurrentBotDisplayId(activeBot.botId);
  //     }
  //   }

  //   // If we don't have active bots but isBotConnected is true (and currentBotType is calendar), update it
  //   if (!hasActiveBots && isBotConnected && currentBotType === 'calendar') {
  //     console.log('📅 No active calendar bots found, setting isBotConnected = false');
  //     setIsBotConnected(false);
  //     setCurrentBotType(null);
  //     setCurrentBotSessionId(null);
  //     setCurrentBotDisplayId(null);
  //     setSelectedBotId(null);
  //   }
  // }, [calendarBots, isBotConnected, currentBotType, selectedBotId]);

  // Alert dismiss functions
  const dismissCompetitorAlert = (index: number) => {
    dispatch(removeCompetitorAlert(index));
  };

  // Services are now connected through websocketService
  // No need for separate connection logic here

  // Check authentication status first
  useEffect(() => {
    if (!isAuthenticated || !tokenInfo?.token) {
      console.log('🔐 User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    if (!isMongoVerified) {
      console.log('🔍 User not verified in MongoDB, redirecting to login page');
      navigate('/login');
      return;
    }
  }, [isAuthenticated, tokenInfo, isMongoVerified, navigate]);

  // Connect to calendar service on mount
  // Note: No cleanup function - the service handles reconnection internally
  // and has duplicate connection prevention. Disconnecting in cleanup causes
  // issues with React StrictMode which unmounts/remounts components.
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      return;
    }

    console.log('📅 Connecting to calendar service on mount with Firebase UID:', user.uid);

    calendarWebSocketService.connect(undefined, user.uid)
      .then(() => {
        console.log('✅ Calendar service connected successfully');
        setIsCalendarConnected(true);
      })
      .catch((error) => {
        console.warn('⚠️ Could not connect to calendar service:', error);
        setIsCalendarConnected(false);
      });

    // No cleanup - let the service manage its own lifecycle
    // The service has duplicate connection prevention, so multiple calls to connect() are safe
  }, [isAuthenticated, user]);

  // Register calendar service callbacks
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      return;
    }

    // Register callback for active bots list (sent on connect)
    calendarWebSocketService.onActiveBots((bots) => {
      // If we received active bots, the calendar service is connected
      setIsCalendarConnected(true);

      setCalendarBots(bots);

      // Check if there are any active/resumable bots
      const activeBots = bots.filter(b => b.status === 'active' && b.botId);

      if (activeBots.length > 0 && !selectedBotId) {
        // Show resume modal if there are active bots and no bot is currently selected
        console.log('📅 Found active bots, showing resume modal:', activeBots.length);
        setResumableBots(activeBots);
        setShowResumeModal(true);
      } else if (activeBots.length === 0) {
        // No active bots, close resume modal if it's open
        setShowResumeModal(false);
        setResumableBots([]);
      }
    });

    // Register callback for new bots going active
    calendarWebSocketService.onBotActive((bot) => {
      console.log('📅 New bot active:', bot);
      console.log('🔍 DEBUG: Bot isSalespersonHost:', bot.isSalespersonHost);
      // Add to list if not already there
      // Match by botId if both have it, otherwise match by botSessionId (handles scheduled → joining transition)
      setCalendarBots(prev => {
        const exists = prev.some(b =>
          (bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)
        );
        if (exists) {
          // Update existing bot (match by botId first, fallback to botSessionId)
          return prev.map(b =>
            ((bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)) ? bot : b
          );
        }
        // Add new bot
        return [...prev, bot];
      });

      // NOTE: Auto-selection removed - user must explicitly click to select a bot
      // This prevents issues with stale closures and multiple bots being selected

      // Dispatch custom event for MeetBotModal to know bot is truly active (not just in lobby)
      console.log('📅 Bot is now active, dispatching bot-active event:', bot.botId);
      window.dispatchEvent(new CustomEvent('bot-active', {
        detail: { botId: bot.botId, status: bot.status }
      }));

      // Auto-close modal when bot becomes active (backup in case modal event listener fails)
      setShowMeetBotModal(false);
    });

    // Register callback for bot status changes
    calendarWebSocketService.onBotStatusChange((bot) => {
      console.log('📅 Bot status changed:', bot);
      console.log('🔍 DEBUG: Bot isSalespersonHost:', bot.isSalespersonHost);

      // Update bot list
      // Match by botId if both have it, otherwise match by botSessionId (handles scheduled → joining transition)
      let updatedBots: CalendarBot[] = [];
      setCalendarBots(prev => {
        const exists = prev.some(b =>
          (bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)
        );
        if (exists) {
          updatedBots = prev.map(b =>
            ((bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)) ? bot : b
          );
        } else {
          // Bot not in list yet (e.g., just moved to 'joining'), add it
          updatedBots = [...prev, bot];
        }
        return updatedBots;
      });

      // NOTE: Auto-selection removed - user must explicitly click to select a bot
      // This prevents issues with stale closures and multiple bots being selected

      // If selected bot terminated, clear selection and bot connected state
      const isSelectedBot = bot.botId === selectedBotId ||
                           bot.botId === currentBotDisplayId;

      // Before anything clears the bot state it reads (connectedBotRef).
      handleBotEndedOutside(bot);

      if (isSelectedBot && bot.status === 'terminated') {
        console.log('📅 Selected bot terminated, clearing selection and bot state');
        setSelectedBotId(null);
        setIsBotConnected(false);
        setCurrentBotType(null);
        setCurrentBotSessionId(null);
        setCurrentBotDisplayId(null);
        setIsSessionCreator(false);
      }


      // If bot just became active and is currently selected, ensure recall connection
      if (bot.botId === selectedBotId && bot.status === 'active' && bot.websocketUrl) {
        const currentRoomId = sessionManager.getRoomId();
        if (currentRoomId === bot.botId) {
          console.log('📅 Bot is now active, ensuring recall connection');
          // Reconnect to ensure we have recall WebSocket
          // The calendar socket lists only this user's bots: the owner.
          handleBotSelection(bot.botId, bot.websocketUrl, bot.botSessionId, undefined, true);
        }
      }
    });

    // No cleanup needed - websocketService manages calendar connection lifecycle
    // the bot-selection state is read inside a long-lived calendar handler; adding it would re-
    // subscribe on every selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  /**
   * Join a bot call: as its owner (Connect Bot, own calendar bot, Resume) or,
   * with `isOwner` false, as a watcher of someone else's bot (read-only; the
   * page hides every owner control). joinBotSession opens the sockets and
   * restores the call so far as silent history.
   */
  const handleBotSelection = async (
    botId: string,
    websocketUrl?: string,
    botSessionId?: string,
    isSalespersonHostParam?: boolean,
    isOwner = false
  ): Promise<boolean> => {
    if (!botId) return false;
    clearBotNotAdmitted();
    setConnectingBotId(botId);

    // The calendar list (only this user's bots) fills in what the caller did not pass.
    const knownBot = calendarBots.find((bot) => bot.botId === botId);
    const resolvedBotSessionId = botSessionId || knownBot?.botSessionId || null;
    const resolvedWebsocketUrl = websocketUrl || knownBot?.websocketUrl;
    const isSalespersonHost = isSalespersonHostParam ?? knownBot?.isSalespersonHost ?? true;

    setSelectedBotId(botId);
    // Set reconnecting flag to prevent redirect
    dispatch(setReconnecting(true));
    try {
      const { clientUpdateWarning, sessionName } = await joinBotSession({
        botId,
        websocketUrl: resolvedWebsocketUrl,
        isSalespersonHost,
        isOwner,
      });
      // An expired session or a bot this user may not join is told, not swallowed.
      if (clientUpdateWarning) setAlertMessage(clientUpdateWarning);

      // Mark as calendar bot for removal
      setCurrentBotType('calendar');
      setCurrentBotSessionId(resolvedBotSessionId); // UUID for API calls
      setCurrentBotDisplayId(botId); // Recall.ai bot ID for display
      setIsSessionCreator(isOwner);
      setWatchedBotSessionName(isOwner ? null : sessionName);
      setIsBotConnected(true);
      return true;
    } catch (error) {
      console.error('❌ Error switching to bot:', error);
      setAlertMessage('Failed to connect to bot. Please try again.');
      return false;
    } finally {
      dispatch(setReconnecting(false));
      setConnectingBotId(null);
    }
  };

  const handleResumeSession = async (bot: CalendarBot) => {
    if (resumingBotId) return;
    console.log('🔄 Resuming session for bot:', bot.botId);
    // The modal stays up saying "Resuming session…" until the join has
    // restored the call's transcript and alerts (owner, 2026-10-07: it used to
    // close at once, with no feedback while they loaded).
    setResumingBotId(bot.botId);
    try {
      // The owner's own working state (the sales playbook, from /resume); the
      // transcript and cards come back as history when the bot joins.
      await restoreOwnPlaybookUsage(bot.botId);
      // Resumable bots come from the calendar socket: this user's own.
      const joined = await handleBotSelection(
        bot.botId,
        bot.websocketUrl,
        bot.botSessionId,
        bot.isSalespersonHost,
        true
      );
      // A failed join has already said so; the modal stays up to retry or start new.
      if (joined) setShowResumeModal(false);
    } catch (error) {
      console.error('❌ Error resuming session:', error);
      setAlertMessage('Failed to resume session. Please try again.');
    } finally {
      setResumingBotId(null);
    }
  };

  const handleDismissResumeModal = () => {
    console.log('🚫 User dismissed resume modal, starting fresh session');
    setShowResumeModal(false);
    setResumableBots([]);
  };

  // Track the true height of the fixed header so the main content margin-top stays exact
  useEffect(() => {
    const el = fixedHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setFixedHeaderHeight(el.offsetHeight));
    observer.observe(el);
    setFixedHeaderHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  const resizeLeftPane = useCallback((clientX: number) => {
    const row = paneRowBox(paneRowRef.current);
    if (!row) return;
    const hotWidth = hotButtonsEnabled ? ((hotPanePct ?? 20) / 100) * row.width + PANE_MIN.handle : 0;
    const max = row.width - PANE_MIN.centre - PANE_MIN.handle - hotWidth;
    const width = Math.min(Math.max(clientX - row.left - PANE_MIN.handle / 2, PANE_MIN.left), max);
    setLeftPanePct((width / row.width) * 100);
  }, [hotButtonsEnabled, hotPanePct]);

  const resizeHotPane = useCallback((clientX: number) => {
    const row = paneRowBox(paneRowRef.current);
    if (!row) return;
    const leftWidth = ((leftPanePct ?? 40) / 100) * row.width + PANE_MIN.handle;
    const max = row.width - leftWidth - PANE_MIN.centre - PANE_MIN.handle;
    const width = Math.min(Math.max(row.left + row.width - clientX - PANE_MIN.handle / 2, PANE_MIN.hot), max);
    setHotPanePct((width / row.width) * 100);
  }, [leftPanePct]);

  // The arrow keys move a handle by 2% of the row.
  const stepPane = (pane: 'left' | 'hot', direction: -1 | 1) => {
    const row = paneRowBox(paneRowRef.current);
    if (!row) return;
    if (pane === 'left') {
      const current = ((leftPanePct ?? (hotButtonsEnabled ? 40 : 50)) / 100) * row.width;
      resizeLeftPane(row.left + current + PANE_MIN.handle / 2 + direction * row.width * 0.02);
    } else {
      const current = ((hotPanePct ?? 20) / 100) * row.width;
      resizeHotPane(row.left + row.width - current - PANE_MIN.handle / 2 + direction * row.width * 0.02);
    }
  };

  // Start speech rate monitoring when connected AND audio capture is active AND authenticated
  useEffect(() => {
    if (isConnected && speechRateSettings.enabled && isAudioCaptureActive && isAuthenticated && tokenInfo?.token) {
      // console.log('🎤 Starting speech rate monitoring...');
      
      speechRateService.startMonitoring(
        () => transcripts, // Get transcripts function
        () => ({ wpm_threshold: speechRateSettings.wpm_threshold }),
        (analysis) => {
          dispatch(updateSpeechRateAnalysis(analysis));
        },
        2000 // Check every 2 seconds
      );

      return () => {
        // console.log('🎤 Stopping speech rate monitoring...');
        speechRateService.stopMonitoring();
      };
    } else if (!isAudioCaptureActive) {
      // Stop monitoring if audio capture is not active
      speechRateService.stopMonitoring();
    }
  }, [isConnected, speechRateSettings.enabled, speechRateSettings.wpm_threshold, transcripts, dispatch, isAudioCaptureActive, isAuthenticated, tokenInfo]);

  // Register cleanup handlers when component mounts (only if authenticated and connected)
  useEffect(() => {
    if (isConnected && isAuthenticated && tokenInfo?.token) {
      cleanupService.registerCleanupHandlers();
      // Start a new conversation session
      const sessionInfo = websocketService.getSessionInfo();
      conversationService.startSession(sessionInfo?.roomId, sessionInfo?.clientId);
    }
    
    return () => {
      // Cleanup when component unmounts
      cleanupService.unregisterCleanupHandlers();
    };
  }, [isConnected, isAuthenticated, tokenInfo]);

  // No redirect based on connection status - users can view the page and connect when ready
  // Authentication is still checked in the initial useEffect above

  const handleSignOut = async () => {
    try {
      // Perform manual cleanup before signing out
      cleanupService.manualCleanup();

      // Clear alerts before signing out
      competitorWebSocketService.clearAlerts();
      landmineWebSocketService.clearAlerts();

      // Reset Redux state
      dispatch(resetConnection());
      dispatch(clearConversation());

      // Sign out from Firebase
      const auth = getAuth();
      await signOut(auth);

      // Dispatch logout action to clear auth state
      dispatch(logoutUser() as any);

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Still navigate to login even if sign out fails
      navigate('/login');
    }
  };

  // TODO(cleanup): unreferenced today. Kept rather than deleted so removing it is a
  // deliberate, reviewed change instead of a side effect of the lint pass.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearFeed = () => {
    if (window.confirm('Are you sure you want to clear the conversation feed?')) {
      dispatch(clearConversation());
    }
  };

  // TODO(cleanup): unreferenced today (an AudioCaptureModal callback; that modal is not
  // rendered by this page). Kept so removing it is a deliberate, reviewed change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCaptureStateChange = (isCapturing: boolean) => {
    setIsAudioCaptureActive(isCapturing);
    // console.log(`🎤 Audio capture state changed: ${isCapturing ? 'ACTIVE' : 'STOPPED'}`);
  };

  // Don't render anything if not authenticated or not verified
  if (!isAuthenticated || !tokenInfo?.token || !isMongoVerified) {
    return null; // Will redirect via useEffect
  }

  // No connection check needed - users can view the page and connect when ready

  return (
    <SessionViewerProvider isWatcher={isWatcher} ownParticipantId={ownParticipantId}>
    <div className="h-screen flex flex-col bg-paper overflow-hidden">
      {/* Fixed Top Bar — includes navbar, speech alert, and info bar */}
      <div ref={fixedHeaderRef} className="fixed top-0 left-0 right-0 z-50">
        <TopBar
          onSignOut={handleSignOut}
          roomId={selectedBotId || watchedStandalone?.roomId || null}
        />

        {/* The server refused a roster edit (only the bot's owner may make one) */}
        <RosterEditNotice />

        {/* Speech Rate Alert Banner: the rep's own coaching, never a watcher's */}
        <SpeechRateBanner />

        {/* Combined info bar — live transcript */}
        <CombinedInfoBar />
      </div>

      {/* Competitor Alerts */}
      <div className="fixed top-32 right-4 z-40 w-96 max-h-[80vh] overflow-y-auto space-y-2">
        {competitorAlerts.map((alert: any, index: number) => (
          <CompetitorAlert
            key={`competitor-alert-${index}`}
            alert={alert}
            onDismiss={() => dismissCompetitorAlert(index)}
          />
        ))}
      </div>

      {/* Main Content Area with proper top margin */}
      <div
        ref={paneRowRef}
        className="flex-1 flex min-h-0 px-2 pt-2 pb-2"
        style={{
          marginTop: `${fixedHeaderHeight}px`
        }}
      >
        {/* Left column: session controls, session status and the Sales Playbook,
            stacked 6 apart (the macOS left panel). Half the row while hot buttons are
            off; two fifths while they are on (HOT_BUTTONS_ENABLED). */}
        <div
          className={`flex min-h-0 min-w-0 flex-col gap-1.5 ${leftPanePct !== null ? 'flex-none' : hotButtonsEnabled ? 'w-2/5' : 'flex-1'}`}
          style={leftPanePct !== null ? { width: `${leftPanePct}%` } : undefined}
        >
          <div className="flex flex-shrink-0 flex-col gap-1.5">
            {/* Session controls: one row of buttons, the same as the desktop apps */}
            <div className="flex flex-wrap items-center gap-2 rounded-card border border-hairline bg-surface px-2.5 py-2">
              {/* Watch a session — same button row as the desktop apps */}
              {!isSessionLive && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowWatchModal(true)}
                  className="flex items-center space-x-1"
                >
                  <CalendarMonthIcon sx={{ fontSize: 16 }} />
                  <span>Watch a session</span>
                </Button>
              )}
              {/* The owner's Connect / Remove Bot; a watcher gets Leave below instead */}
              {!isWatcher && (
                <Button
                  variant={isBotConnected ? 'destructive' : 'primary'}
                  size="sm"
                  onClick={() => {
                    console.log('🚀 Opening MeetBotModal with state:', {
                      isBotConnected,
                      currentBotType,
                      currentBotSessionId,
                      currentBotDisplayId,
                      selectedBotId
                    });
                    setShowMeetBotModal(true);
                  }}
                  className="flex items-center space-x-1"
                >
                  <SmartToyIcon sx={{ fontSize: 16 }} />
                  <span>{isBotConnected ? 'Remove Bot' : 'Connect Bot'}</span>
                </Button>
              )}
              {/* Reopen the participant roster on demand; the bot's owner only. */}
              <UserRosterButton
                isBotConnected={isBotConnected}
                isBotOwner={isSessionCreator}
                onOpen={() => setRosterOpenRequest((n) => n + 1)}
              />
              {/* Manual Audio Capture button - disabled when any bot or standalone session is active */}
              {/* <button
                onClick={() => setShowManualCaptureModal(true)}
                disabled={isBotConnected || isStandaloneSessionConnected || calendarBots.some(bot =>
                  bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                )}
                className={`${
                  isBotConnected || isStandaloneSessionConnected || calendarBots.some(bot =>
                    bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                  )
                    ? 'bg-surface-sunken cursor-not-allowed text-ink-disabled'
                    : 'bg-signature text-on-signature hover:bg-signature-strong'
                } px-4 py-2 rounded-md transition-colors flex items-center space-x-2`}
                title={
                  isStandaloneSessionConnected
                    ? 'Desktop app session is active - stop it first to use manual capture'
                    : isBotConnected || calendarBots.some(bot =>
                        bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                      )
                      ? 'Disable bot connection first to use manual capture'
                      : 'Capture audio directly from your computer'
                }
              >
                <span>Manual Capture</span>
              </button> */}
              {/* Someone else's call: only Leave; the status card says co-seller or observer */}
              {isCoSeller ? (
                <CoSellerStatus onLeave={handleLeaveWatching} />
              ) : (
                isWatcher && <WatcherStatus onLeave={handleLeaveWatching} />
              )}
            </div>

            {/* Session status, only while there is one: the live session header (two
                rows, the same as macOS and WPF, owner 2026-10-07), or why the bot was
                never let into the meeting, until Dismiss or the next session. The
                border is the status colour at 35%: live green, otherwise amber. */}
            {(isSessionLive || botNotAdmittedReason) && (
              <div
                className="space-y-1 rounded-card border bg-surface px-2.5 py-2"
                style={{
                  borderColor: `color-mix(in srgb, var(${isSessionLive ? '--success' : '--warning'}) 35%, transparent)`,
                }}
              >
                {isSessionLive && (
                  <LiveSessionHeader
                    sessionName={liveSessionName}
                    getCallStartTime={getCallStartTime}
                    viewerRole={liveViewerRole}
                  />
                )}
                {botNotAdmittedReason && (
                  <BotNotAdmittedNotice reason={botNotAdmittedReason} onDismiss={clearBotNotAdmitted} />
                )}
              </div>
            )}
          </div>

          {/* Sales Playbook Panel — fills the rest of the left column down to the bottom
              and scrolls inside, as on macOS (no measured height, no resize handle).
              The checklist is the call owner's: a watcher sees it read-only. */}
          <SalesPlaybookPanel
            fillHeight
            isSessionActive={isBotConnected && isSessionCreator}
            viewOnlyTitle={isWatcher ? WATCHER_COPY.checklistViewOnly : undefined}
          />
        </div>

        <PaneResizeHandle
          label="Resize the playbook and insights panes"
          onDrag={resizeLeftPane}
          onStep={(direction) => stepPane('left', direction)}
        />

        {/* Centre - Live Call Insights: the other half of the row. Hot buttons, while the
            service has them on (HOT_BUTTONS_ENABLED), take the last fifth (40/40/20). */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ConversationFeed />
        </div>
        {hotButtonsEnabled && (
          <>
            <PaneResizeHandle
              label="Resize the insights and hot buttons panes"
              onDrag={resizeHotPane}
              onStep={(direction) => stepPane('hot', direction)}
            />
            <div
              className={`flex min-h-0 min-w-0 flex-col ${hotPanePct !== null ? 'flex-none' : 'w-1/5'}`}
              style={hotPanePct !== null ? { width: `${hotPanePct}%` } : undefined}
            >
              <HotButtonFocus canDismiss={isSessionLive && !isWatcher} />
            </div>
          </>
        )}
      </div>



      <AlertModal isOpen={alertMessage !== null} message={alertMessage ?? ''} onClose={() => setAlertMessage(null)} />

      {/* A coaching socket has been retrying for 2 s: block the live view until
          every one is back (same overlay as macOS and WPF). The bot's owner can
          still end the call through the Remove Bot flow; a watcher can Leave. */}
      <LiveReconnectOverlay
        isSessionActive={isSessionLive}
        isSessionOwner={isBotConnected && isSessionCreator}
        isEndFlowOpen={showMeetBotModal}
        onEndSession={openMeetBotModal}
        onLeave={handleLeaveWatching}
        leaveLabel={isCoSeller ? COSELLER_MIC_COPY.leaveCall : undefined}
      />

      {/* Watch a Session: your meetings, and every live call in the tenant */}
      <WatchSessionModal
        isOpen={showWatchModal}
        onClose={() => setShowWatchModal(false)}
        calendarBots={calendarBots}
        connectingSessionId={connectingBotId ?? joiningStandaloneId}
        joinError={standaloneJoinError}
        onConnect={handleBotSelection}
        onWatchStandalone={handleWatchStandalone}
        onJoinWithMic={handleJoinWithMic}
        isJoiningWithMic={isJoiningWithMic}
      />

      <MeetBotModal
        isOpen={showMeetBotModal}
        isBotConnected={isBotConnected}
        botType={currentBotType}
        botSessionId={currentBotSessionId}
        botDisplayId={currentBotDisplayId}
        setCalendarBots={setCalendarBots}
        defaultBotName={calendarSettings?.botName}
        isSessionCreator={isSessionCreator}
        onSessionEnded={handleSessionEnded}
        onBotNotAdmitted={handleBotNotAdmitted}
        onBotConnectionStateChange={(isConnected) => {
          console.log(`🔄 Bot connection state changed to: ${isConnected ? 'connected' : 'disconnected'}`);
          setIsBotConnected(isConnected);

          // Note: Bot status is managed by calendar_sync_service - no manual update needed

          if (!isConnected) {
            // Clear bot type and session info when disconnected
            setCurrentBotType(null);
            setCurrentBotSessionId(null);
            setCurrentBotDisplayId(null);
            setIsSessionCreator(false);
            setSelectedBotId(null);
            setConnectingBotId(null);
          }
        }}
        onClose={() => {
          console.log('🔒 Modal closed by user or after successful connection');
          setShowMeetBotModal(false);
        }}
        onBotCreated={async (botId, websocketUrl) => {
          console.log(`🤖 Bot created with bot_id: ${botId}`);
          clearBotNotAdmitted();
          console.log(`🔌 WebSocket URL received: ${websocketUrl}`);

          try {
            // Set reconnecting flag to prevent redirect during reconnection
            dispatch(setReconnecting(true));
            console.log('🔄 Set isReconnecting flag to prevent redirect');

            // Import services
            const { sessionManager } = await import('../services/sessionManager');
            const { websocketService } = await import('../services/websocketService');
            const { recallWebSocketService } = await import('../services/recallWebSocketService');

            // Update sessionManager with the bot_id
            sessionManager.setRoomId(botId);
            console.log(`✅ Updated sessionManager room_id to: ${botId}`);

            // Connect to Recall WebSocket service
            console.log(`🔌 Connecting to Recall WebSocket service at: ${websocketUrl}`);
            await recallWebSocketService.connect(botId, websocketUrl);

            // Reconnect all other WebSocket services with the new room_id
            console.log(`🔄 Reconnecting all services with new room_id: ${botId}`);
            await websocketService.reconnectWithNewRoomId();

            console.log(`✅ All services connected with bot_id "${botId}" as room_id`);

            // Mark as manual bot for removal
            setCurrentBotType('manual');
            setCurrentBotSessionId(botId); // For manual bots, botId is the session ID
            setCurrentBotDisplayId(botId); // Same for display
            setIsSessionCreator(true);

            // Auto-select the manual bot so it shows data immediately
            setSelectedBotId(botId);
            console.log(`✅ Auto-selected manual bot: ${botId}`);

            // DON'T mark bot as connected here - wait for the modal to notify us
            // via onBotConnectionStateChange callback after the bot actually joins
            // setIsBotConnected(true);

            // Clear reconnecting flag
            dispatch(setReconnecting(false));
            console.log('✅ Cleared isReconnecting flag');

            // DON'T close the modal here - let the MeetBotModal handle its own closing
            // after the bot actually joins the meeting
            // setShowMeetBotModal(false);
          } catch (error) {
            console.error('❌ Error reconnecting services:', error);

            // Clear reconnecting flag on error
            dispatch(setReconnecting(false));

            setAlertMessage('Failed to connect services with bot. Please try disconnecting and reconnecting.');
            // Don't close modal on error - let user see the error message in the modal
            // setShowMeetBotModal(false);
          }
        }}
      />

      {/* Resume Session Modal */}
      <ResumeSessionModal
        isOpen={showResumeModal}
        bots={resumableBots}
        onResume={handleResumeSession}
        onDismiss={handleDismissResumeModal}
        resumingBotId={resumingBotId}
      />

      {/* Manual Audio Capture Modal */}
      <ManualCaptureModal
        isOpen={showManualCaptureModal}
        onClose={() => setShowManualCaptureModal(false)}
      />

      {/* End of a bot call: participant review (creator), then the summary */}
      <EndOfCallModals flow={endOfCallFlow} />

      {/* Multi-party: confirm participant roles + names in real time (bot mode) */}
      <ParticipantRolesModal
        enabled={!!selectedBotId}
        isBotOwner={isSessionCreator}
        openRequest={rosterOpenRequest}
        ownParticipantId={ownBotParticipantId}
        onMarkOwn={setMarkedOwnParticipantId}
      />
    </div>
    </SessionViewerProvider>
  );
};

export default ConversationPage;
